import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Tab, Tabs } from '@mui/material';
import { toast } from '@/components/core/v2/toaster';

import { ReservationsCalendar } from './components/reservations-calendar';
import { MyReservations } from './components/my-reservations';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reservations-tabpanel-${index}`}
      aria-labelledby={`reservations-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export function ReservationsPage(): React.JSX.Element {
  const [currentTab, setCurrentTab] = React.useState(0);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');

    if (payment === 'success') {
      toast.success('Deposit paid! Your reservation has been created.');
      setCurrentTab(1); // Switch to "My Reservations" tab
      // Refetch so the new reservation appears immediately
      void queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      void queryClient.invalidateQueries({ queryKey: ['blocked-slots'] });
    } else if (payment === 'cancel') {
      toast.info('Payment cancelled. No reservation was created.');
      setCurrentTab(0); // Stay on booking tab so they can try again
    }

    if (payment) {
      params.delete('payment');
      params.delete('reservation_id');
      const newQuery = params.toString();
      const newUrl = newQuery ? `${window.location.pathname}?${newQuery}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [queryClient]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="reservations tabs">
          <Tab label="Reserve Amenity" id="reservations-tab-0" />
          <Tab label="My Reservations" id="reservations-tab-1" />
        </Tabs>
      </Box>

      <TabPanel value={currentTab} index={0}>
        <ReservationsCalendar />
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <MyReservations />
      </TabPanel>
    </Box>
  );
}
